import { useState } from "react";
import { Reveal } from "./ui-bits";

const INTERESTS = [
  "Foundation plan — tablets (£99/mo)",
  "Precision plan — creams or depot injections (£129/mo)",
  "Specialist plan — premium injectables (£179/mo)",
  "Not sure yet — advise me",
  "Switching from another TRT clinic",
  "Blood testing only",
  "A question before I book",
];

export default function LeadForm({ defaultInterest }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    interest: defaultInterest || INTERESTS[3],
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | sending | done | error
  const [errorMsg, setErrorMsg] = useState("");

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending");
    setErrorMsg("");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Request failed");
      setStatus("done");
    } catch (err) {
      setStatus("error");
      setErrorMsg("Something went wrong sending your enquiry. Please try again.");
    }
  };

  if (status === "done") {
    return (
      <div
        className="card-line rounded-2xl p-8 text-center"
        data-testid="lead-form-success"
      >
        <div className="w-12 h-12 rounded-full bg-teal/10 border border-teal/40 grid place-items-center mx-auto mb-5">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M4 12.5 9.5 18 20 6.5" stroke="#00E5C0" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <h3 className="font-display text-3xl tracking-wide">Enquiry received</h3>
        <p className="text-mute mt-3 max-w-md mx-auto leading-relaxed">
          Thank you, {form.name.split(" ")[0]}. A member of the team will be in touch to explain
          your assessment options. No treatment is promised or implied at this stage.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="card-line rounded-2xl p-6 sm:p-8 space-y-4" data-testid="lead-capture-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="lf-name" className="mono-label text-dusk block mb-2">Full name *</label>
          <input id="lf-name" required className="field" placeholder="Your name" value={form.name} onChange={set("name")} data-testid="lead-form-input-name" />
        </div>
        <div>
          <label htmlFor="lf-email" className="mono-label text-dusk block mb-2">Email *</label>
          <input id="lf-email" type="email" required className="field" placeholder="you@example.com" value={form.email} onChange={set("email")} data-testid="lead-form-input-email" />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="lf-phone" className="mono-label text-dusk block mb-2">Phone (optional)</label>
          <input id="lf-phone" type="tel" className="field" placeholder="Best number to reach you" value={form.phone} onChange={set("phone")} data-testid="lead-form-input-phone" />
        </div>
        <div>
          <label htmlFor="lf-interest" className="mono-label text-dusk block mb-2">I'm interested in</label>
          <select id="lf-interest" className="field" value={form.interest} onChange={set("interest")} data-testid="lead-form-input-interest">
            {INTERESTS.map((i) => (
              <option key={i} value={i} className="bg-surface">{i}</option>
            ))}
          </select>
        </div>
      </div>
      <div>
        <label htmlFor="lf-message" className="mono-label text-dusk block mb-2">Anything we should know? (optional)</label>
        <textarea id="lf-message" rows={4} className="field resize-none" placeholder="Symptoms, how long they've lasted, current medication, or your question…" value={form.message} onChange={set("message")} data-testid="lead-form-input-message" />
      </div>
      {status === "error" && (
        <p className="text-rose text-sm" data-testid="lead-form-error">{errorMsg}</p>
      )}
      <button type="submit" className="btn-primary w-full justify-center" disabled={status === "sending"} data-testid="lead-form-submit">
        {status === "sending" ? "Sending…" : "Request my assessment options"}
      </button>
      <p className="text-xs text-dusk leading-relaxed">
        Your details are used only to respond to your enquiry. Sending this form does not create a
        patient relationship, and no treatment is promised at this stage.
      </p>
    </form>
  );
}
